import { BuilderContent } from '@builder.io/sdk';

const BUILDER_CDN_RE = /(?:https?:)?\/\/cdn\.builder\.io\//;
const SRCSET_WIDTHS = [400, 800, 1200, 1600];
const DEFAULT_SIZES = '(max-width: 768px) 100vw, (max-width: 1200px) 100vw, 1200px';

/**
 * Regex that matches an `<img` opening tag, correctly handling quoted attribute values.
 * Captures the full attributes string so we can inspect / augment it.
 */
const IMG_TAG_RE = /<img\b((?:[^>"']|"[^"]*"|'[^']*')*)\/?>/gi;

function getAttr(attrs: string, name: string): string | null {
  const re = new RegExp(`${name}\\s*=\\s*(?:"([^"]*)"|'([^']*)')`, 'i');
  const m = attrs.match(re);
  return m ? (m[1] ?? m[2]) : null;
}

function hasAttr(attrs: string, name: string): boolean {
  return new RegExp(`\\b${name}\\s*=`, 'i').test(attrs);
}

function setBuilderWidth(url: string, width: number): string {
  const urlToParse = url.startsWith('//') ? `https:${url}` : url;
  try {
    const u = new URL(urlToParse);
    u.searchParams.set('width', String(width));
    return url.startsWith('//') ? u.toString().replace('https:', '') : u.toString();
  } catch {
    return url;
  }
}

function setBuilderFormat(url: string, format: string): string {
  const urlToParse = url.startsWith('//') ? `https:${url}` : url;
  try {
    const u = new URL(urlToParse);
    u.searchParams.set('format', format);
    u.searchParams.set('quality', '80');
    return url.startsWith('//') ? u.toString().replace('https:', '') : u.toString();
  } catch {
    return url;
  }
}

function setBuilderQuality(url: string): string {
  const urlToParse = url.startsWith('//') ? `https:${url}` : url;
  try {
    const u = new URL(urlToParse);
    u.searchParams.set('quality', '80');
    return url.startsWith('//') ? u.toString().replace('https:', '') : u.toString();
  } catch {
    return url;
  }
}

function buildSrcSet(src: string): string {
  return SRCSET_WIDTHS.map((w) => {
    const sized = setBuilderWidth(src, w);
    return `${setBuilderQuality(sized)} ${w}w`;
  }).join(', ');
}

function buildWebPSrcSet(src: string): string {
  return SRCSET_WIDTHS.map((w) => {
    const sized = setBuilderWidth(src, w);
    return `${setBuilderFormat(sized, 'webp')} ${w}w`;
  }).join(', ');
}

/**
 * Takes an HTML string and optimises any `<img>` tags that reference the
 * Builder CDN.  Adds srcset, sizes, loading, decoding, and wraps in a
 * `<picture>` element with a WebP `<source>` for next-gen format support.
 */
export function optimizeImagesInHtml(html: string): string {
  return html.replace(IMG_TAG_RE, (fullMatch, rawAttrs: string) => {
    const src = getAttr(rawAttrs, 'src');
    if (!src || !BUILDER_CDN_RE.test(src)) return fullMatch;

    let attrs = rawAttrs;

    if (!hasAttr(attrs, 'srcset')) {
      attrs += ` srcset="${buildSrcSet(src)}"`;
    }
    if (!hasAttr(attrs, 'sizes')) {
      attrs += ` sizes="${DEFAULT_SIZES}"`;
    }
    if (!hasAttr(attrs, 'loading')) {
      attrs += ' loading="lazy"';
    }
    if (!hasAttr(attrs, 'decoding')) {
      attrs += ' decoding="async"';
    }

    const optimizedImg = `<img${attrs} />`;
    const webpSrcSet = buildWebPSrcSet(src);

    return `<picture><source type="image/webp" srcset="${webpSrcSet}" sizes="${DEFAULT_SIZES}" />${optimizedImg}</picture>`;
  });
}

// ---------------------------------------------------------------------------
// Content-tree walker
// ---------------------------------------------------------------------------

interface BuilderBlock {
  component?: { name?: string; options?: Record<string, unknown> };
  children?: BuilderBlock[];
  [key: string]: unknown;
}

function walkBlocks(blocks: BuilderBlock[]): BuilderBlock[] {
  let changed = false;
  const result = blocks.map((block) => {
    let blockChanged = false;
    let newBlock = block;

    if (block.component?.name === 'Text' && typeof block.component.options?.text === 'string') {
      const original = block.component.options.text;
      const optimized = optimizeImagesInHtml(original);
      if (optimized !== original) {
        newBlock = {
          ...block,
          component: {
            ...block.component,
            options: { ...block.component.options, text: optimized },
          },
        };
        blockChanged = true;
      }
    }

    if (Array.isArray(block.children) && block.children.length > 0) {
      const newChildren = walkBlocks(block.children);
      if (newChildren !== block.children) {
        newBlock = blockChanged ? { ...newBlock, children: newChildren } : { ...block, children: newChildren };
        blockChanged = true;
      }
    }

    if (blockChanged) changed = true;
    return newBlock;
  });

  return changed ? result : blocks;
}

/**
 * Walks a Builder content object's block tree and optimises any images found
 * inside Text blocks. Returns the original reference when nothing changes.
 */
export function optimizeContentImages(content: BuilderContent): BuilderContent {
  const blocks = content?.data?.blocks as BuilderBlock[] | undefined;
  if (!Array.isArray(blocks) || blocks.length === 0) return content;

  const optimizedBlocks = walkBlocks(blocks);
  if (optimizedBlocks === blocks) return content;

  return {
    ...content,
    data: { ...content.data, blocks: optimizedBlocks as unknown as NonNullable<BuilderContent['data']>['blocks'] },
  };
}
